import React, { useEffect, useMemo, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useHistory } from 'react-router-dom'
import { ABSpinner, useErrorService } from '@abroad/components'
import {
  ExperienceHomeCard,
  HomeAttentionRow,
  HomeSectionHead,
  PlatformShortcutCard,
} from '../components/home'
import {
  CreateExperienceMenu,
  ExperiencePageHead,
} from '../components/experienceUI'
import {
  getHomeAttentionItems,
  getHomeExperienceCards,
  getPortfolioStats,
} from '../components/home/homeExperienceData'
import { getExperienceScenario } from '../components/Quests/experienceStatus'
import API from '../utils/API'

const EXPERIENCES_PATH = '/admin/experiences'

const formatCount = (value) =>
  typeof value === 'number' ? value.toLocaleString() : value

/** v7 `.platform-grid` — same destinations and counts as the old count cards. */
const getPlatformShortcuts = (counts) => [
  {
    key: 'users',
    glyph: '●',
    tone: 'orange',
    title: 'Users',
    meta: formatCount(counts?.usersCount),
    to: '/admin/users',
  },
  {
    key: 'organizations',
    glyph: '▦',
    tone: 'green',
    title: 'Organizations',
    meta: formatCount(counts?.companiesCount),
    to: '/admin/organizations',
  },
  {
    key: 'networks',
    glyph: '⌘',
    tone: 'red',
    title: 'Network',
    meta: formatCount(counts?.ecosystemCount),
    to: '/admin/networks',
  },
  {
    key: 'groups',
    glyph: '●●',
    tone: 'pink',
    title: 'Groups',
    meta: formatCount(counts?.teamsCount),
    to: '/admin/groups',
  },
  {
    key: 'coaches',
    glyph: '▣',
    tone: 'cyan',
    title: 'Coaches',
    meta: formatCount(counts?.coachesCount),
    to: '/admin/coaches',
  },
  {
    key: 'promo',
    glyph: '%',
    tone: 'orange',
    title: 'Active Promo codes',
    meta: formatCount(counts?.promoCount) ?? 'Manage',
    to: '/admin/promo',
  },
  {
    key: 'assessment',
    glyph: '%',
    tone: 'mint',
    title: 'Assessment',
    meta: formatCount(counts?.assessmentCount),
    to: null,
  },
  {
    key: 'scheduled-emails',
    glyph: '✉',
    tone: 'coral',
    title: 'Email Sequencing',
    meta: 'Manage',
    to: '/admin/scheduled-emails',
  },
  {
    key: 'account',
    glyph: '▤',
    tone: 'purple',
    title: 'Account',
    meta: 'Settings',
    to: '/admin/account',
  },
  {
    key: 'payment',
    glyph: '$',
    tone: 'blue',
    title: 'Payment',
    meta: 'Manage',
    to: '/admin/payment',
  },
]

const Home = () => {
  const history = useHistory()
  const Error = useErrorService()
  const [isLoading, setIsLoading] = useState(true)
  const [counts, setCounts] = useState(null)
  const [quests, setQuests] = useState([])

  useEffect(() => {
    let cancelled = false

    const loadHome = async () => {
      try {
        // Same two existing admin endpoints: platform counts + experience list.
        const [countResult, questResult] = await Promise.all([
          API.AdminAPI.home.getUserInfo(),
          API.AdminAPI.quests.getQuestList({ isArchived: false }),
        ])
        if (cancelled) {
          return
        }
        setCounts(countResult?.data || null)
        const list = questResult?.data
        setQuests(
          Array.isArray(list)
            ? list
            : Array.isArray(list?.quests)
            ? list.quests
            : [],
        )
      } catch (e) {
        if (!cancelled) {
          Error.showError(e)
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false)
        }
      }
    }

    loadHome()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const experienceCards = useMemo(
    () => getHomeExperienceCards(quests),
    [quests],
  )
  const attentionItems = useMemo(() => getHomeAttentionItems(quests), [quests])
  const portfolioStats = useMemo(() => getPortfolioStats(quests), [quests])
  const platformShortcuts = useMemo(
    () => getPlatformShortcuts(counts),
    [counts],
  )

  const openExperience = (quest) => {
    const id = quest?._id || quest?.id
    if (!id) {
      return
    }
    const scenario = getExperienceScenario(quest)
    history.push(
      scenario === 'draft' || scenario === 'new'
        ? `${EXPERIENCES_PATH}/${id}/build/setup-name`
        : `${EXPERIENCES_PATH}/${id}/overview`,
    )
  }

  if (isLoading) {
    return <ABSpinner />
  }

  return (
    <main className='experience experience-home'>
      <Helmet>
        <title>{'Home | Abroad'}</title>
        <meta name='title' content={'Home | Abroad'}></meta>
        <meta property='og:title' content={'Home | Abroad'}></meta>
        <meta property='twitter:title' content={'Home | Abroad'}></meta>
        <link
          rel='canonical'
          href={`${process.env.REACT_APP_DOMAIN}/admin/home`}></link>
        <meta
          property='og:url'
          content={`${process.env.REACT_APP_DOMAIN}/admin/home`}></meta>
        <meta
          property='twitter:url'
          content={`${process.env.REACT_APP_DOMAIN}/admin/home`}></meta>
      </Helmet>

      <ExperiencePageHead
        title='Home'
        subtitle='What needs attention across the Client Platform today.'>
        <button
          type='button'
          className='btn-outline-experience'
          onClick={() => history.push(EXPERIENCES_PATH)}>
          All experiences
        </button>
        <CreateExperienceMenu />
      </ExperiencePageHead>

      <div className='experience-home-wrap'>
        <section className='experience-home-section'>
          <HomeSectionHead
            kicker='Experiences'
            title='Current + upcoming'
            description='The three nearest experiences by start date. Status stays visible so drafts are not mistaken for live trips.'>
            <button
              type='button'
              className='experience-linkbtn experience-home-view-all'
              onClick={() => history.push(EXPERIENCES_PATH)}>
              View all experiences →
            </button>
          </HomeSectionHead>
          {experienceCards.length > 0 ? (
            <div className='experience-home-grid'>
              {experienceCards.map(({ quest, scenario }, index) => (
                <ExperienceHomeCard
                  key={quest._id || quest.id}
                  quest={quest}
                  scenario={scenario}
                  index={index}
                  onOpen={() => openExperience(quest)}
                />
              ))}
            </div>
          ) : (
            <div className='experience-home-attention-empty'>
              <b>No current or upcoming experiences</b>
              <span>
                Create an experience to see it here alongside its readiness.
              </span>
            </div>
          )}
        </section>

        <section className='experience-home-section experience-home-split'>
          {attentionItems.length > 0 ? (
            <div>
              <HomeSectionHead
                kicker='Today'
                title='Needs attention'
                description='Only items that have a useful next action.'
                compact
              />
              <div className='experience-home-attention-card'>
                {attentionItems.map((item) => (
                  <HomeAttentionRow
                    key={item.key}
                    icon={item.icon}
                    tone={item.tone}
                    title={item.title}
                    detail={item.detail}
                    action={item.action}
                    onClick={() => openExperience(item.quest)}
                  />
                ))}
              </div>
            </div>
          ) : null}

          <aside className='experience-home-summary-card'>
            <span className='experience-home-kicker'>Experience portfolio</span>
            <h2>At a glance</h2>
            <div className='experience-home-portfolio-stats'>
              {portfolioStats.map((stat) => (
                <div key={stat.key}>
                  <b>{stat.total}</b>
                  <span>{stat.label}</span>
                </div>
              ))}
            </div>
            <button
              type='button'
              className='btn-outline-experience'
              onClick={() => history.push(EXPERIENCES_PATH)}>
              Open experience list
            </button>
          </aside>
        </section>

        <section className='experience-home-section experience-home-platform-section'>
          <HomeSectionHead
            kicker='Platform'
            title='Admin shortcuts'
            description='Jump to other Client Platform areas.'
          />
          <div className='experience-home-platform-grid'>
            {platformShortcuts.map((item) => (
              <PlatformShortcutCard
                key={item.key}
                glyph={item.glyph}
                tone={item.tone}
                title={item.title}
                meta={item.meta}
                to={item.to}
              />
            ))}
          </div>
        </section>
      </div>
    </main>
  )
}

export default Home
